/**
 * The drawer of one inquiry on the page "Learn from sales" (lead decision S7): the facts of
 * the link (quotation, F&O status, sales order, sales person or "Not shown", how linked,
 * the lines matched), the line difference ("Line by line"), "What I2Q learns from this"
 * with "Approve" and "Retract" for a manager, and "Open the case".
 *
 * The drawer opens from the address (`?case=`), so a link shows the same inquiry. It reads
 * `GET /sales-truth/inquiries/{case_id}`: 404 for an inquiry outside the areas of the
 * session or without a link. `InquiryDetailBody` is the content on its own, so a test can
 * render it without the drawer. `linkFactItems` is shared with the "Quoted in F&O" block of
 * the case page.
 *
 * Round 3 (20 Oct 2026, goal A): a link whose sales order has its lines read adds "Ordered
 * lines" ("14 of 16 quoted lines ordered") and "The draft had the ordered items" to the
 * facts, marks each quoted line "Ordered" or "Not ordered", and lists the order lines that
 * were not on the quotation when I2Q linked it. Round 4 (21 Oct 2026): with the lines of the
 * sales order a quoted line is "Ordered", "Removed" or "Cancelled", the facts add "Changed
 * after the order", and the lists "Added on the sales order" and the credit lines follow;
 * the quotation lines at the order carry a note that I2Q has not read the sales order lines.
 *
 * Round 3 (20 Oct 2026, goal B): under "Sales order", the "Customer PO" of the sales order
 * (for every area: business data, no person) and "In purchase order mail" with the date of
 * the purchase order mail of the customer that names it (the first with an order intent,
 * else the first), a link to that case when it is in the areas of the session.
 */

import {
  Badge,
  Button,
  DescriptionList,
  type DescriptionListEntry,
  Drawer,
  EmptyState,
  Skeleton,
  Typography,
} from "@norican/react";
import { Link } from "react-router";

import { CardError } from "@/features/dashboard/parts";
import { useSalesTruthInquiry } from "@/lib/api";
import { formatDate, formatXofY } from "@/lib/format";
import { logger } from "@/lib/logger";
import { sampleSalesTruthInquiryDetail } from "@/lib/mock";

import { AgreementBar, CustomerMatch, LinkCell } from "./inquiries-tab";
import {
  CELL_LABELS,
  DRAWER_LABELS,
  LEARNING_LABELS,
  LFS_NOTICE_LABELS,
  ORDER_LINE_LABELS,
  orderChangesText,
  orderedLinesText,
  PO_LABELS,
} from "./labels";
import { LearningTable, MultiAccountNote } from "./learnings-tab";
import { LineDiffTable, OrderExtras } from "./line-diff-table";
import { type GroundTruth, type InquiryDetail, toInquiryDetail } from "./normalize";
import { dayOf, personText, quoteStatusLabel, quoteStatusTone } from "./view";

/** True only in the Vite dev server; statically `false` in a production build. */
const DEV_FIXTURES = import.meta.env.DEV;

/** The facts of a link, as the drawer and the case page list them. */
export function linkFactItems(link: GroundTruth): DescriptionListEntry[] {
  const items: DescriptionListEntry[] = [
    {
      label: DRAWER_LABELS.quotation,
      value: link.quotation ? (
        <span className="flex flex-wrap items-center gap-2">
          <span className="break-all">{link.quotation}</span>
          {link.quote_status && (
            <Badge tone={quoteStatusTone(link.quote_status)} variant="outline" size="sm">
              {quoteStatusLabel(link.quote_status)}
            </Badge>
          )}
        </span>
      ) : (
        CELL_LABELS.none
      ),
    },
  ];
  if (link.quote_created_at) {
    items.push({ label: DRAWER_LABELS.quoteMade, value: formatDate(dayOf(link.quote_created_at)) });
  }
  items.push({ label: DRAWER_LABELS.order, value: link.sales_order ?? CELL_LABELS.none });
  items.push(...poFactItems(link));
  items.push(...orderFactItems(link));
  items.push(
    {
      label: DRAWER_LABELS.person,
      value: (
        <span data-person-hidden={link.person_hidden ? "" : undefined}>{personText(link)}</span>
      ),
    },
    {
      label: DRAWER_LABELS.linked,
      value: <LinkCell source={link.source} certainty={link.certainty} />,
    },
    {
      label: DRAWER_LABELS.agreement,
      value: <AgreementBar share={link.line_agreement} lines={link.line_count} />,
    },
  );
  if (link.modified_after_reply) {
    items.push({ label: DRAWER_LABELS.changed, value: CELL_LABELS.changedAfterReply });
  }
  return items;
}

/** "Customer PO" and "In purchase order mail" (round 3, goal B); none without a PO. */
export function poFactItems(link: GroundTruth): DescriptionListEntry[] {
  if (!link.customer_po) {
    return [];
  }
  const items: DescriptionListEntry[] = [
    {
      label: PO_LABELS.customerPo,
      value: (
        <span className="break-all" data-customer-po="">
          {link.customer_po}
        </span>
      ),
    },
  ];
  if (link.po_mail_at) {
    const day = formatDate(dayOf(link.po_mail_at));
    items.push({
      label: PO_LABELS.poMail,
      value: link.po_mail_case_id ? (
        <Link
          to={`/case/${encodeURIComponent(link.po_mail_case_id)}`}
          className="text-accent-fg-subtle hover:underline"
          title={PO_LABELS.poMailHint}
          data-po-mail={link.po_mail_case_id}
        >
          {day}
        </Link>
      ) : (
        <span data-po-mail="">{`${PO_LABELS.poMailOtherArea}, ${day}`}</span>
      ),
    });
  }
  return items;
}

/**
 * The facts of the order lines (round 3); none while the link has no order lines. With the
 * lines of the sales order (round 4) and a change after the order: "Changed after the
 * order" with the counts.
 */
export function orderFactItems(link: GroundTruth): DescriptionListEntry[] {
  if (link.ordered_quoted === null || link.order_lines_total === null) {
    return [];
  }
  const quoted = link.line_count ?? link.diff.filter((row) => row.ordered !== null).length;
  const changes = orderChanges(link);
  return [
    ...(changes === null
      ? []
      : [
          {
            label: ORDER_LINE_LABELS.changed,
            value: <span data-order-changes="">{changes}</span>,
          },
        ]),
    {
      label: ORDER_LINE_LABELS.orderedLines,
      value: <span data-ordered-lines="">{orderedLinesText(link.ordered_quoted, quoted)}</span>,
    },
    {
      label: ORDER_LINE_LABELS.draftHad,
      value: (
        <span className="tabular-nums" data-ordered-found="">
          {formatXofY(link.order_lines_found ?? 0, link.order_lines_total)}
        </span>
      ),
    },
  ];
}

/** "Removed 1, cancelled 0, quantity changed 1, added 2", or null without a change. */
export function orderChanges(link: GroundTruth): string | null {
  if (link.order_lines_source !== "sales_order") {
    return null;
  }
  const removed = link.order_removed ?? 0;
  const cancelled = link.order_cancelled ?? 0;
  const qty = link.order_qty_changed.length;
  const added = link.order_only.length;
  if (removed + cancelled + qty + added === 0) {
    return null;
  }
  return orderChangesText(removed, cancelled, qty, added);
}

/** The content of the drawer for one answer. */
export function InquiryDetailBody({
  detail,
  canDecide,
}: {
  detail: InquiryDetail;
  canDecide: boolean;
}) {
  const { inquiry, link, learnings } = detail;
  const head: DescriptionListEntry[] = [
    {
      label: DRAWER_LABELS.received,
      value: inquiry.received_at ? formatDate(dayOf(inquiry.received_at)) : CELL_LABELS.notKnown,
    },
    { label: DRAWER_LABELS.area, value: inquiry.area.toUpperCase() },
    { label: DRAWER_LABELS.customer, value: <CustomerMatch row={inquiry} /> },
  ];
  return (
    <div className="flex min-w-0 flex-col gap-5" data-lfs-drawer={inquiry.case_id}>
      {inquiry.subject && (
        <Typography variant="body-sm" className="break-words">
          {inquiry.subject}
        </Typography>
      )}
      <section className="flex flex-col gap-2" aria-label={DRAWER_LABELS.facts}>
        <Typography as="h3" variant="label" className="text-fg">
          {DRAWER_LABELS.facts}
        </Typography>
        <DescriptionList items={[...head, ...linkFactItems(link)]} />
        {inquiry.open_in_i2q && (
          <Badge tone="warning" variant="subtle" size="sm" className="self-start">
            {CELL_LABELS.openInI2q}
          </Badge>
        )}
      </section>
      <section className="flex flex-col gap-2" aria-label={DRAWER_LABELS.diff}>
        <Typography as="h3" variant="label" className="text-fg">
          {DRAWER_LABELS.diff}
        </Typography>
        <LineDiffTable rows={link.diff} counts={link.diff_counts} cards />
        <OrderExtras link={link} />
      </section>
      <section className="flex flex-col gap-2" aria-label={DRAWER_LABELS.learns}>
        <Typography as="h3" variant="label" className="text-fg">
          {DRAWER_LABELS.learns}
        </Typography>
        {learnings.length === 0 ? (
          <Typography variant="caption" tone="muted" data-lfs-no-learnings="">
            {LFS_NOTICE_LABELS.noLearnings}
          </Typography>
        ) : (
          <LearningTable
            rows={learnings}
            canDecide={canDecide}
            applyOn={detail.apply_on}
            withCase={false}
            cards
          />
        )}
        {canDecide && learnings.length > 0 && !detail.apply_on && (
          <Typography variant="caption" tone="muted" data-lfs-apply-off="">
            {LEARNING_LABELS.applyOff}
          </Typography>
        )}
        <MultiAccountNote
          senders={learnings.some((row) => row.sender_multi_account === true) ? 1 : 0}
          switchOn={detail.multi_account_on}
          canDecide={canDecide}
          hasCustomers={learnings.some((row) => row.kind === "customer")}
        />
      </section>
      <Button
        size="sm"
        variant="outline"
        tone="neutral"
        className="self-start"
        render={<Link to={`/case/${encodeURIComponent(inquiry.case_id)}`} />}
        data-lfs-case-link=""
      >
        {DRAWER_LABELS.openCase}
      </Button>
    </div>
  );
}

/** The drawer itself: open while the address names an inquiry. */
export function InquiryDrawer({
  caseId,
  canDecide,
  onClose,
}: {
  caseId: string | null;
  canDecide: boolean;
  onClose: () => void;
}) {
  const inquiry = useSalesTruthInquiry(caseId);
  const status = (inquiry.error as { status?: number } | null)?.status;
  // Local dev without a backend: the sample inquiry, like the tabs of the page.
  const sample =
    DEV_FIXTURES && inquiry.error && status !== 403 && status !== 404
      ? sampleSalesTruthInquiryDetail()
      : null;
  const data = inquiry.data ?? sample;
  return (
    <Drawer.Root
      open={caseId !== null}
      side="right"
      onOpenChange={(open) => {
        logger.debug("[learn-from-sales] drawer", { case: caseId, open });
        if (!open) {
          onClose();
        }
      }}
    >
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Viewport>
          <Drawer.Popup className="w-[min(100vw,40rem)]">
            <Drawer.Header>
              <Drawer.Title>
                <Typography as="span" variant="heading-6" className="text-fg">
                  {DRAWER_LABELS.title}
                </Typography>
              </Drawer.Title>
              <Drawer.CloseButton label={DRAWER_LABELS.close} />
            </Drawer.Header>
            <Drawer.Body className="overflow-y-auto">
              {inquiry.isLoading ? (
                <Skeleton variant="rect" height={240} />
              ) : status === 404 ? (
                <EmptyState title={LFS_NOTICE_LABELS.notFound} />
              ) : data ? (
                <InquiryDetailBody detail={toInquiryDetail(data)} canDecide={canDecide} />
              ) : inquiry.error ? (
                <CardError error={inquiry.error} onRetry={() => void inquiry.refetch()} />
              ) : null}
            </Drawer.Body>
          </Drawer.Popup>
        </Drawer.Viewport>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
